import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Pagination,
  Stack,
  LinearProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Search,
  Edit,
  Delete,
  Visibility,
  PersonAdd,
  Download,
  Refresh,
  VerifiedUser,
  Block,
  Email,
  Phone,
  AccountBalance,
} from '@mui/icons-material';
import { 
  collection, 
  getDocs, 
  doc, 
  updateDoc, 
  deleteDoc, 
  query, 
  orderBy, 
  where 
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import { motion } from 'framer-motion';

const UsersManagement = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const usersPerPage = 10;

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    // Filter users based on search term
    const filtered = users.filter(user => 
      user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.mobileNumber?.includes(searchTerm) ||
      user.xmAccountNumber?.includes(searchTerm) ||
      user.weltradeAccountNumber?.includes(searchTerm)
    );
    setFilteredUsers(filtered);
    setCurrentPage(1);
  }, [searchTerm, users]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const usersQuery = query(collection(db, 'users'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(usersQuery);
      const usersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date(doc.data().createdAt)
      }));
      setUsers(usersData);
    } catch (error) {
      console.error('Error fetching users:', error);
      showSnackbar('Error fetching users', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleEditUser = (user) => {
    setSelectedUser({ ...user });
    setDialogOpen(true);
  };

  const handleSaveUser = async () => {
    try {
      const userRef = doc(db, 'users', selectedUser.id);
      const updateData = {
        firstName: selectedUser.firstName,
        lastName: selectedUser.lastName,
        mobileNumber: selectedUser.mobileNumber,
        isVerified: selectedUser.isVerified,
        xmAccountNumber: selectedUser.xmAccountNumber || null,
        weltradeAccountNumber: selectedUser.weltradeAccountNumber || null,
        tradingFinancials: selectedUser.tradingFinancials || false,
        tradingSynthetics: selectedUser.tradingSynthetics || false,
        updatedAt: new Date(),
      };
      
      await updateDoc(userRef, updateData);
      
      // Update local state
      setUsers(prev => prev.map(user => 
        user.id === selectedUser.id ? { ...user, ...updateData } : user
      ));
      
      setDialogOpen(false);
      showSnackbar('User updated successfully');
    } catch (error) {
      console.error('Error updating user:', error);
      showSnackbar('Error updating user', 'error');
    }
  };

  const handleDeleteUser = async () => {
    try {
      await deleteDoc(doc(db, 'users', userToDelete.id));
      setUsers(prev => prev.filter(user => user.id !== userToDelete.id));
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      showSnackbar('User deleted successfully');
    } catch (error) {
      console.error('Error deleting user:', error);
      showSnackbar('Error deleting user', 'error');
    }
  };

  const exportUsers = () => {
    const csvContent = [
      ['Name', 'Email', 'Mobile', 'XM Account', 'Weltrade Account', 'Verified', 'Created'].join(','),
      ...filteredUsers.map(user => [
        `"${user.firstName} ${user.lastName}"`,
        `"${user.email}"`,
        `"${user.mobileNumber || ''}"`,
        `"${user.xmAccountNumber || ''}"`,
        `"${user.weltradeAccountNumber || ''}"`,
        user.isVerified ? 'Yes' : 'No',
        user.createdAt?.toLocaleDateString() || ''
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `fxwars-users-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Pagination
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">
          Users Management
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button
            startIcon={<Download />}
            variant="outlined"
            onClick={exportUsers}
            disabled={filteredUsers.length === 0}
          >
            Export CSV
          </Button>
          <Button
            startIcon={<Refresh />}
            variant="outlined"
            onClick={fetchUsers}
            disabled={loading}
          >
            Refresh
          </Button>
        </Stack>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" variant="overline">Total Users</Typography>
              <Typography variant="h4" fontWeight="bold">{users.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" variant="overline">Verified Users</Typography>
              <Typography variant="h4" fontWeight="bold">
                {users.filter(u => u.isVerified).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" variant="overline">XM Accounts</Typography>
              <Typography variant="h4" fontWeight="bold">
                {users.filter(u => u.xmAccountNumber).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" variant="overline">Weltrade Accounts</Typography>
              <Typography variant="h4" fontWeight="bold">
                {users.filter(u => u.weltradeAccountNumber).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search users by name, email, phone, or account number..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {/* Users Table */}
      <Paper>
        {loading ? (
          <LinearProgress />
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Contact</TableCell>
                    <TableCell>Trading Accounts</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Joined</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentUsers.map((user, index) => (
                    <motion.tr
                      key={user.id}
                      component={TableRow}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                            {user.firstName?.[0] || user.email[0].toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2">
                              {user.firstName} {user.lastName}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              {user.email}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.5}>
                          {user.mobileNumber && (
                            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                              <Phone sx={{ fontSize: 16, mr: 0.5 }} />
                              {user.mobileNumber}
                            </Typography>
                          )}
                          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                            <Email sx={{ fontSize: 16, mr: 0.5 }} />
                            {user.email}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.5}>
                          {user.xmAccountNumber && (
                            <Chip 
                              label={`XM: ${user.xmAccountNumber}`} 
                              size="small" 
                              color="primary"
                              variant="outlined"
                            />
                          )}
                          {user.weltradeAccountNumber && (
                            <Chip 
                              label={`Weltrade: ${user.weltradeAccountNumber}`} 
                              size="small" 
                              color="secondary"
                              variant="outlined"
                            />
                          )}
                          {!user.xmAccountNumber && !user.weltradeAccountNumber && (
                            <Typography variant="body2" color="textSecondary">
                              No accounts
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Chip 
                            label={user.isVerified ? 'Verified' : 'Unverified'} 
                            size="small" 
                            color={user.isVerified ? 'success' : 'default'}
                          />
                          {user.tradingFinancials && (
                            <Chip label="Financials" size="small" variant="outlined" />
                          )}
                          {user.tradingSynthetics && (
                            <Chip label="Synthetics" size="small" variant="outlined" />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {user.createdAt?.toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <Tooltip title="Edit User">
                            <IconButton 
                              size="small" 
                              onClick={() => handleEditUser(user)}
                              color="primary"
                            >
                              <Edit />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete User">
                            <IconButton 
                              size="small" 
                              onClick={() => {
                                setUserToDelete(user);
                                setDeleteDialogOpen(true);
                              }}
                              color="error"
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </motion.tr>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={(e, page) => setCurrentPage(page)}
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </Paper>

      {/* Edit User Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Stack spacing={3} sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="First Name"
                    value={selectedUser.firstName || ''}
                    onChange={(e) => setSelectedUser(prev => ({ ...prev, firstName: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    value={selectedUser.lastName || ''}
                    onChange={(e) => setSelectedUser(prev => ({ ...prev, lastName: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email"
                    value={selectedUser.email || ''}
                    disabled
                    helperText="Email cannot be changed"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Mobile Number"
                    value={selectedUser.mobileNumber || ''}
                    onChange={(e) => setSelectedUser(prev => ({ ...prev, mobileNumber: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="XM Account Number"
                    value={selectedUser.xmAccountNumber || ''}
                    onChange={(e) => setSelectedUser(prev => ({ ...prev, xmAccountNumber: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Weltrade Account Number"
                    value={selectedUser.weltradeAccountNumber || ''}
                    onChange={(e) => setSelectedUser(prev => ({ ...prev, weltradeAccountNumber: e.target.value }))}
                  />
                </Grid>
              </Grid>
              
              <Stack spacing={2}>
                <FormControlLabel
                  control={
                    <Switch 
                      checked={selectedUser.isVerified || false}
                      onChange={(e) => setSelectedUser(prev => ({ ...prev, isVerified: e.target.checked }))}
                    />
                  }
                  label="Verified User"
                />
                <FormControlLabel
                  control={
                    <Switch 
                      checked={selectedUser.tradingFinancials || false}
                      onChange={(e) => setSelectedUser(prev => ({ ...prev, tradingFinancials: e.target.checked }))}
                    />
                  }
                  label="Trading Financials"
                />
                <FormControlLabel
                  control={
                    <Switch 
                      checked={selectedUser.tradingSynthetics || false}
                      onChange={(e) => setSelectedUser(prev => ({ ...prev, tradingSynthetics: e.target.checked }))}
                    />
                  }
                  label="Trading Synthetics"
                />
              </Stack>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveUser} variant="contained">Save Changes</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete user "{userToDelete?.firstName} {userToDelete?.lastName}"? 
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteUser} color="error" variant="contained">
            Delete User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UsersManagement; 